
import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { PlaceResult } from '@/types/place';
import SavedSearches from '@/components/SavedSearches';
import LeadQuotaIndicator from '@/components/LeadQuotaIndicator';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { CheckCircle, X } from 'lucide-react';

// Import our refactored components
import SearchForm from './SearchForm';
import SearchResults from './SearchResults';
import MobileStickyBar from './components/MobileStickyBar';
import { usePlacesSearch } from './hooks/usePlacesSearch';

const Places = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const searchFormRef = useRef<HTMLDivElement>(null);
  const [quotaRefreshTrigger, setQuotaRefreshTrigger] = useState(0);
  const [recentlyImported, setRecentlyImported] = useState<Array<{id: string, name: string}>>([]);
  
  const { 
    searchQuery,
    setSearchQuery,
    locationQuery,
    setLocationQuery,
    radius,
    setRadius,
    useRadius,
    setUseRadius,
    results,
    isSearching,
    isLoadingMore,
    hasSearched,
    nextPageToken,
    searchMeta,
    activeSavedSearch,
    handleSearch,
    handleLoadMore,
    handleLoadSavedSearch,
    handleImport,
    clearSearch
  } = usePlacesSearch(() => {
    // Refresh the quota indicator when a lead is successfully imported
    setQuotaRefreshTrigger(prev => prev + 1);
  });

  // Pass through the Lead object from handleImport and track imports
  const handleImportWrapper = async (place: PlaceResult) => {
    const lead = await handleImport(place);
    if (lead?.id) {
      setRecentlyImported(prev => [...prev, { id: lead.id, name: place.name }]);
    }
    return lead;
  };

  return (
    <div className="flex-1 container mx-auto p-4 md:p-6 pb-20">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-2">Find Leads</h1>
          <p className="text-muted-foreground">Search for businesses and import them as leads.</p>
        </div>
        
        {user && (
          <SavedSearches
            onLoadSearch={handleLoadSavedSearch}
            currentSearch={{
              query: searchQuery,
              location: locationQuery,
              radius
            }}
            resultCount={results.length}
          />
        )}
      </div>
      
      <div className="mb-6">
        <LeadQuotaIndicator refreshTrigger={quotaRefreshTrigger} />
      </div>
      
      {recentlyImported.length > 0 && (
        <Alert className="mb-4 bg-green-50 border-green-600 dark:bg-green-950/20">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800 dark:text-green-400">
            {recentlyImported.length} Lead{recentlyImported.length > 1 ? 's' : ''} Imported
          </AlertTitle>
          <AlertDescription>
            <div className="flex flex-wrap gap-2 mt-2 items-center">
              <Button 
                variant="outline" 
                size="sm" 
                asChild
                className="text-green-600 border-green-600 hover:bg-green-50 dark:hover:bg-green-950/40"
              >
                <Link to="/app">View All Leads ({recentlyImported.length})</Link>
              </Button>
              
              <div className="hidden md:flex gap-2 flex-wrap">
                {recentlyImported.slice(0, 3).map(lead => (
                  <Button 
                    key={lead.id}
                    variant="ghost" 
                    size="sm" 
                    asChild
                    className="text-green-600 hover:bg-green-50 dark:hover:bg-green-950/40"
                  >
                    <Link to={`/leads/${lead.id}`}>{lead.name}</Link>
                  </Button>
                ))}
                {recentlyImported.length > 3 && (
                  <span className="text-sm text-muted-foreground self-center">
                    +{recentlyImported.length - 3} more
                  </span>
                )}
              </div>
              
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setRecentlyImported([])}
                className="ml-auto"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}
      
      <div ref={searchFormRef}>
        <SearchForm
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          locationQuery={locationQuery}
          setLocationQuery={setLocationQuery}
          radius={radius}
          setRadius={setRadius}
          useRadius={useRadius}
          setUseRadius={setUseRadius}
          onSearch={handleSearch}
          isSearching={isSearching}
          hasSearched={hasSearched}
          onClearSearch={clearSearch}
        />
      </div>
      
      {hasSearched && (
        <SearchResults 
          results={results}
          isSearching={isSearching}
          searchQuery={searchQuery}
          locationQuery={locationQuery}
          radius={radius}
          hasSearched={hasSearched}
          searchMeta={searchMeta}
          activeSavedSearch={activeSavedSearch}
          onImport={handleImportWrapper}
          nextPageToken={nextPageToken}
          isLoadingMore={isLoadingMore}
          onLoadMore={handleLoadMore}
          onRetry={() => handleSearch()}
        />
      )}
      
      <MobileStickyBar 
        hasSearched={hasSearched}
        resultCount={results.length}
        importedCount={recentlyImported.length}
        onScrollToSearch={() => searchFormRef.current?.scrollIntoView({ behavior: 'smooth' })}
        onViewImported={() => navigate('/app')}
      />
    </div>
  );
};

export default Places;
